import React, { useState } from 'react';
import { AlertCircle, Waves } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Wave 2 / marine-zone auto-suggest (2026-02) — COLORSTEEL warning
 * banner + operator override picker.
 *
 * Data model:
 *   - `environment_suggested` — auto-derived from postcode marine zone.
 *     Stamped by property write cascades + appraisal GET lazy-backfill.
 *   - `environment_final` — operator override. When set, it wins over
 *     the suggestion. Set to "" to revert to auto.
 *
 * Effective zone = environment_final || environment_suggested.
 * Marine + severe-marine trigger the COLORSTEEL warning.
 *
 * The picker PATCHes `/appraisals/{id}/inline-field` (whitelist already
 * updated) so no full-appraisal PUT is needed. On save we optimistically
 * update the local field via `onUpdated`.
 */

const ZONE_LABELS = {
  '':               'Auto (use suggested)',
  'inland':         'Inland',
  'semi-marine':    'Semi-marine',
  'marine':         'Marine',
  'severe-marine':  'Severe-marine',
};

const ZONE_ORDER = ['', 'inland', 'semi-marine', 'marine', 'severe-marine'];

const isSevere = (z) => z === 'marine' || z === 'severe-marine';

export default function EnvironmentZoneCard({ appraisal, api, onUpdated }) {
  const suggested = (appraisal?.environment_suggested || '').trim();
  const final = (appraisal?.environment_final || '').trim();
  const effective = final || suggested;
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);

  // Nothing to say if we have no data at all (e.g. property has no address yet).
  if (!effective && !suggested && !final) return null;

  const isOverridden = !!final && final !== suggested;
  const showSevereBanner = isSevere(effective);

  const save = async (nextValue) => {
    if (saving) return;
    setSaving(true);
    try {
      await api.patch(`/appraisals/${appraisal._id}/inline-field`, {
        field: 'environment_final',
        value: nextValue,
      });
      // Optimistic local update — no full reload needed.
      onUpdated?.({ ...appraisal, environment_final: nextValue });
      toast.success(nextValue
        ? `Environment set to ${ZONE_LABELS[nextValue]}`
        : `Reverted to auto-suggested (${ZONE_LABELS[suggested] || 'unknown'})`);
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save environment override');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      data-testid="env-zone-card"
      className={`mb-3 rounded-md border-l-4 px-3 py-2 text-xs ${
        showSevereBanner
          ? 'border-rose-500 bg-rose-50 text-rose-900'
          : 'border-slate-300 bg-slate-50 text-slate-700'
      }`}
    >
      <div className="flex items-start gap-2">
        {showSevereBanner
          ? <AlertCircle size={14} className="text-rose-700 mt-0.5 shrink-0" />
          : <Waves size={14} className="text-slate-500 mt-0.5 shrink-0" />}
        <div className="flex-1">
          {showSevereBanner ? (
            <div data-testid="colorsteel-warning">
              <span className="font-semibold">COLORSTEEL warning · {ZONE_LABELS[effective]} zone.</span>
              {' '}Salt exposure at this address is significant — verify product suitability + warranty terms before quoting COLORSTEEL Endura / Maxx. Consider Marine-grade alternatives.
            </div>
          ) : (
            <div>
              <span className="font-semibold">Environment:</span>{' '}
              {ZONE_LABELS[effective] || 'Unknown'}
              {isOverridden && (
                <span className="ml-2 rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-medium text-slate-700">
                  operator override
                </span>
              )}
              {!final && suggested && (
                <span className="ml-2 rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-medium text-slate-700">
                  auto-suggested
                </span>
              )}
            </div>
          )}
          {isOverridden && showSevereBanner && (
            <div className="mt-1 text-[11px] opacity-80">
              Auto-suggested was <em>{ZONE_LABELS[suggested] || 'unknown'}</em>; operator flipped it to <em>{ZONE_LABELS[final]}</em>.
            </div>
          )}
        </div>
        <button
          data-testid="env-zone-override-btn"
          type="button"
          disabled={saving}
          onClick={() => setOpen((v) => !v)}
          className={`shrink-0 rounded px-2 py-1 text-[11px] font-medium ${
            showSevereBanner
              ? 'bg-white/70 text-rose-900 hover:bg-white'
              : 'bg-white text-slate-700 hover:bg-slate-100'
          } border border-current/20 disabled:opacity-50`}
        >
          {open ? 'Cancel' : 'Override'}
        </button>
      </div>
      {open && (
        <div
          data-testid="env-zone-picker"
          className="mt-2 flex flex-wrap gap-1.5 pl-6"
        >
          {ZONE_ORDER.map((z) => {
            const isCurrent = z === final || (z === '' && !final);
            return (
              <button
                key={z || 'auto'}
                type="button"
                disabled={saving || isCurrent}
                onClick={() => save(z)}
                data-testid={`env-zone-option-${z || 'auto'}`}
                className={`rounded-full px-2.5 py-1 text-[11px] font-medium transition ${
                  isCurrent
                    ? 'bg-slate-800 text-white cursor-default'
                    : 'bg-white text-slate-700 hover:bg-slate-200 border border-slate-300'
                }`}
              >
                {ZONE_LABELS[z]}
                {z === suggested && z !== '' && (
                  <span className="ml-1 text-[10px] opacity-70">(auto)</span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
